import * as THREE from 'three';

type SceneOptions = {
  reducedMotion: boolean;
};

const GOLD = 0xd5b46b;
const LIGHT = 0xdfe5ef;
const NAVY = 0x0b1830;

/** Small deterministic PRNG so the network layout is identical on every load. */
function seeded(seed: number) {
  let s = seed;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Builds a slowly rotating 3D network: gold nodes (talent) and light nodes (organizations)
 * joined by fine gold connections, with a few soft pulses travelling between them.
 * Returns a cleanup function that disposes all GPU resources.
 */
export function createNetworkScene(container: HTMLElement, { reducedMotion }: SceneOptions) {
  let renderer: THREE.WebGLRenderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
  } catch {
    return () => undefined;
  }

  const width = () => Math.max(container.clientWidth, 1);
  const height = () => Math.max(container.clientHeight, 1);

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width(), height());
  renderer.domElement.style.display = 'block';
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.fog = new THREE.Fog(NAVY, 10, 17);

  const camera = new THREE.PerspectiveCamera(42, width() / height(), 0.1, 50);
  camera.position.set(0, 0, 12.5);

  const group = new THREE.Group();
  group.rotation.x = 0.28;
  scene.add(group);

  // --- Nodes -------------------------------------------------------------
  const rand = seeded(11);
  const count = 54;
  const points: THREE.Vector3[] = [];
  const isTalent: boolean[] = [];
  for (let i = 0; i < count; i++) {
    const u = rand() * 2 - 1;
    const theta = rand() * Math.PI * 2;
    const r = 2.6 + rand() * 1.3;
    const s = Math.sqrt(1 - u * u);
    points.push(new THREE.Vector3(r * s * Math.cos(theta) * 1.25, r * u * 0.85, r * s * Math.sin(theta)));
    isTalent.push(i % 3 !== 0);
  }

  const dummy = new THREE.Object3D();
  const goldGeo = new THREE.SphereGeometry(0.085, 16, 16);
  const lightGeo = new THREE.SphereGeometry(0.065, 16, 16);
  const goldMat = new THREE.MeshBasicMaterial({ color: GOLD });
  const lightMat = new THREE.MeshBasicMaterial({ color: LIGHT });
  const talentCount = isTalent.filter(Boolean).length;
  const goldNodes = new THREE.InstancedMesh(goldGeo, goldMat, talentCount);
  const lightNodes = new THREE.InstancedMesh(lightGeo, lightMat, count - talentCount);

  let gi = 0;
  let li = 0;
  points.forEach((p, i) => {
    dummy.position.copy(p);
    dummy.updateMatrix();
    if (isTalent[i]) goldNodes.setMatrixAt(gi++, dummy.matrix);
    else lightNodes.setMatrixAt(li++, dummy.matrix);
  });
  group.add(goldNodes, lightNodes);

  // --- Connections -------------------------------------------------------
  const edges: [number, number][] = [];
  const degree = new Array(count).fill(0);
  for (let i = 0; i < count; i++) {
    for (let j = i + 1; j < count; j++) {
      if (degree[i] >= 4 || degree[j] >= 4) continue;
      if (points[i].distanceTo(points[j]) < 2.0) {
        edges.push([i, j]);
        degree[i]++;
        degree[j]++;
      }
    }
  }
  const linePositions = new Float32Array(edges.length * 6);
  edges.forEach(([a, b], k) => {
    linePositions.set([points[a].x, points[a].y, points[a].z, points[b].x, points[b].y, points[b].z], k * 6);
  });
  const lineGeo = new THREE.BufferGeometry();
  lineGeo.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
  const lineMat = new THREE.LineBasicMaterial({ color: GOLD, transparent: true, opacity: 0.3 });
  group.add(new THREE.LineSegments(lineGeo, lineMat));

  // --- Pulses travelling along connections -------------------------------
  const pulseGeo = new THREE.SphereGeometry(0.05, 12, 12);
  const pulseMat = new THREE.MeshBasicMaterial({ color: 0xf1dfb3 });
  const pulseEdges = edges.filter((_, k) => k % Math.max(1, Math.floor(edges.length / 7)) === 0).slice(0, 7);
  const pulses = pulseEdges.map((edge, k) => {
    const mesh = new THREE.Mesh(pulseGeo, pulseMat);
    group.add(mesh);
    return { mesh, edge, offset: k / pulseEdges.length };
  });

  const placePulses = (t: number) => {
    pulses.forEach(({ mesh, edge, offset }) => {
      const progress = (t * 0.22 + offset) % 1;
      mesh.position.lerpVectors(points[edge[0]], points[edge[1]], progress);
    });
  };

  // --- Interaction & loop ------------------------------------------------
  const pointer = { x: 0, y: 0 };
  const tilt = { x: 0, y: 0 };
  const finePointer = window.matchMedia('(pointer: fine)').matches;
  const onPointerMove = (e: PointerEvent) => {
    const rect = container.getBoundingClientRect();
    pointer.x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    pointer.y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
  };
  if (finePointer && !reducedMotion) window.addEventListener('pointermove', onPointerMove, { passive: true });

  const clock = new THREE.Clock();
  let frame = 0;
  let visible = true;

  const render = () => {
    const t = clock.getElapsedTime();
    tilt.x += (pointer.y * 0.12 - tilt.x) * 0.04;
    tilt.y += (pointer.x * 0.18 - tilt.y) * 0.04;
    group.rotation.y = t * 0.07 + tilt.y;
    group.rotation.x = 0.28 + Math.sin(t * 0.25) * 0.04 + tilt.x;
    placePulses(t);
    renderer.render(scene, camera);
  };

  const loop = () => {
    if (!visible || document.hidden) {
      frame = 0;
      return;
    }
    render();
    frame = requestAnimationFrame(loop);
  };

  const start = () => {
    if (reducedMotion || frame) return;
    frame = requestAnimationFrame(loop);
  };

  if (reducedMotion) {
    placePulses(0.4);
    renderer.render(scene, camera);
  } else {
    start();
  }

  const io = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    if (visible) start();
  });
  io.observe(container);

  const onVisibility = () => {
    if (!document.hidden) start();
  };
  document.addEventListener('visibilitychange', onVisibility);

  const ro = new ResizeObserver(() => {
    renderer.setSize(width(), height());
    camera.aspect = width() / height();
    camera.position.z = width() < 420 ? 14 : 12.5;
    camera.updateProjectionMatrix();
    if (reducedMotion) renderer.render(scene, camera);
  });
  ro.observe(container);

  return () => {
    cancelAnimationFrame(frame);
    io.disconnect();
    ro.disconnect();
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pointermove', onPointerMove);
    [goldGeo, lightGeo, lineGeo, pulseGeo].forEach((g) => g.dispose());
    [goldMat, lightMat, lineMat, pulseMat].forEach((m) => m.dispose());
    goldNodes.dispose();
    lightNodes.dispose();
    renderer.dispose();
    renderer.domElement.remove();
  };
}
